import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, MessageSquareQuote, ArrowLeft, ArrowRight } from "lucide-react";
import Reveal from "./Reveal";
import { useSite } from "@/context/SiteContext";

function CardFace({ t, featured }) {
  return (
    <div
      className={`h-full rounded-2xl bg-white p-7 flex flex-col text-left transition-[box-shadow,border-color] duration-500 ease-out ${featured
        ? "border-[1.5px] border-[#00CFFF] shadow-[0_36px_70px_rgba(7,20,54,0.35),0_16px_34px_rgba(10,22,64,0.22),0_0_0_1px_rgba(0,199,255,0.30),0_0_46px_rgba(0,199,255,0.38),0_0_90px_rgba(0,170,255,0.16)]"
        : "border border-[#E2E9F2] shadow-[0_10px_26px_rgba(13,33,84,0.08)]"}`}
      data-testid={featured ? "testimonial-card-active" : "testimonial-card-side"}
    >
      <div className="flex gap-1">
        {Array.from({ length: Math.min(t.rating || 5, 5) }).map((_, i) => (
          <Star key={i} size={featured ? 20 : 17} className="text-amber-400 fill-amber-400" />
        ))}
      </div>
      <p className={`mt-4 leading-relaxed text-[#47546E] italic ${featured ? "text-[16px]" : "text-[14.5px]"}`}>“{t.quote}”</p>
      <div className="mt-auto pt-6">
        <div className={`font-display font-extrabold text-[#0F172A] ${featured ? "text-[17px]" : "text-[15px]"}`}>{t.name}</div>
        <div className="text-[13px] text-[#64748B] mt-0.5">{t.country}</div>
      </div>
    </div>
  );
}

function Carousel({ items }) {
  const n = items.length;
  const [active, setActive] = useState(n);
  const [snap, setSnap] = useState(false);

  useEffect(() => {
    if (n < 2 || snap) return;
    const t = setTimeout(() => setActive((a) => a + 1), 4200);
    return () => clearTimeout(t);
  }, [n, snap, active]);

  // seamless infinite loop: snap back inside the clone zone after each slide settles
  useEffect(() => {
    if (n >= 2 && active >= 2 * n) {
      const t = setTimeout(() => { setSnap(true); setActive((a) => a - n); }, 850);
      return () => clearTimeout(t);
    }
    if (n >= 2 && active < n) {
      const t = setTimeout(() => { setSnap(true); setActive((a) => a + n); }, 850);
      return () => clearTimeout(t);
    }
  }, [active, n]);

  useEffect(() => {
    if (!snap) return;
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setSnap(false)));
    return () => cancelAnimationFrame(raf);
  }, [snap]);

  const [spacing, setSpacing] = useState(410);
  useEffect(() => {
    const update = () => setSpacing(window.innerWidth < 640 ? Math.min(window.innerWidth * 0.86, 440) + 18 : 410);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const doubled = [...items, ...items];
  const position = ((active % n) + n) % n;

  return (
    <div className="relative" data-testid="testimonial-carousel">
      <div className="relative h-[300px] sm:h-[270px]">
        {doubled.map((t, i) => {
          const off = i - active;
          const dist = Math.abs(off);
          const isCenter = off === 0;
          return (
            <motion.div
              key={i}
              className="absolute top-0 left-1/2 w-[86vw] max-w-[380px] h-full -ml-[43vw] sm:-ml-[190px]"
              style={{ zIndex: isCenter ? 20 : 10 }}
              animate={{
                x: off * spacing,
                y: isCenter ? -10 : 0,
                scale: isCenter ? 1 : 0.94,
                opacity: dist <= 1 ? 1 : 0,
              }}
              transition={snap ? { duration: 0 } : { duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
            >
              <CardFace t={t} featured={isCenter} />
            </motion.div>
          );
        })}
      </div>

      <div className="flex items-center justify-center gap-4 mt-9">
        <button
          onClick={() => setActive((a) => a - 1)}
          aria-label="Previous story"
          className="w-11 h-11 rounded-full border-[1.5px] border-[#00B4E6] text-[#00A3E0] flex items-center justify-center hover:bg-[rgba(0,180,230,0.08)] transition-colors duration-200"
          data-testid="testimonial-prev"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex items-center gap-2.5">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(n + i)}
              aria-label={`Go to story ${i + 1}`}
              className={`h-2.5 rounded-full transition-all duration-300 ${i === position ? "w-7 bg-[#00CFFF]" : "w-2.5 bg-[#0F172A]/20 hover:bg-[#0F172A]/40"}`}
              data-testid={`testimonial-dot-${i}`}
            />
          ))}
        </div>
        <button
          onClick={() => setActive((a) => a + 1)}
          aria-label="Next story"
          className="w-11 h-11 rounded-full border-[1.5px] border-[#00B4E6] text-[#00A3E0] flex items-center justify-center hover:bg-[rgba(0,180,230,0.08)] transition-colors duration-200"
          data-testid="testimonial-next"
        >
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

export default function Testimonials() {
  const { content } = useSite();
  const visible = (content.testimonials || []).filter((t) => t.visible !== false);
  const embeds = [content.embeds?.trustpilot_embed, content.embeds?.google_embed].filter(Boolean);

  return (
    <section
      className="section-pad relative overflow-hidden text-[#0F172A]"
      style={{ backgroundImage: "url(/stories-bg.png)", backgroundSize: "cover", backgroundPosition: "center" }}
      data-testid="testimonials-section"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ backgroundImage: "url(/stories-bg.png)", backgroundSize: "cover", backgroundPosition: "center", filter: "blur(22px)", transform: "scale(1.12)" }}
      />
      <div aria-hidden="true" className="absolute inset-0 bg-white/85" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10">
        <Reveal direction="left">
          <div className="eyebrow text-[#0096D6] mb-4">Client stories</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-[46px] tracking-tight leading-[1.12]">
            Real businesses. <span className="text-[#00A3E0]">Meaningful support.</span>
          </h2>
          <p className="mt-4 text-[#47546E] text-base sm:text-lg max-w-2xl">
            Feedback from our clients, published with their permission.
          </p>
        </Reveal>

        <div className="mt-14">
          {visible.length > 0 ? (
            <Carousel items={visible} />
          ) : (
            <Reveal direction="up">
              <div className="rounded-3xl py-20 flex flex-col items-center text-center px-6 bg-white border border-[#E2E9F2] shadow-[0_14px_40px_rgba(13,33,84,0.08)] max-w-2xl mx-auto" data-testid="testimonials-placeholder">
                <span className="w-16 h-16 rounded-full bg-[#E8F6FC] flex items-center justify-center">
                  <MessageSquareQuote size={26} className="text-[#00A3E0]" />
                </span>
                <h3 className="mt-6 font-display font-extrabold text-xl text-[#0F172A]">Client stories coming soon.</h3>
                <p className="mt-2 text-sm text-[#5A6B8C] max-w-sm">
                  We're working with amazing businesses and will be sharing their stories here soon.
                </p>
              </div>
            </Reveal>
          )}
        </div>

        {embeds.length > 0 && (
          <div className="relative mt-12 grid md:grid-cols-2 gap-6">
            {embeds.map((e, i) => (
              <div key={i} className="rounded-2xl bg-white border border-[#E2E9F2] p-6 [&>iframe]:w-full" dangerouslySetInnerHTML={{ __html: e }} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
